import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { QUERY_KEY } from './keys';
import { queryClient } from './queryClient';
import { clmApiClient } from '../clients';
import type { ApiErrorResponse, DepartmentDetails } from '../types';
import { isApiErrorResponse } from '../utils';

const fetchDepartmentDetails = async (
  departmentId: string | undefined,
): Promise<DepartmentDetails> => {
  if (!departmentId) {
    throw new Error('Department ID is required to fetch department details');
  }

  const response = await clmApiClient.get<DepartmentDetails, ApiErrorResponse>(
    `department/${departmentId}`,
  );

  if (!response.data) {
    throw new Error('No data returned from department details fetch');
  }

  if (!response.ok) {
    if (isApiErrorResponse(response.data)) {
      throw new Error(response.data.details);
    }
    throw new Error('Unknown error');
  }

  return response.data;
};

export const useDepartmentDetails = (
  departmentId: string | undefined,
  config?: Omit<UseQueryOptions<DepartmentDetails>, 'queryKey' | 'queryFn'>,
) =>
  useQuery({
    queryKey: [QUERY_KEY.DEPARTMENT_DETAILS, departmentId],
    queryFn: () => fetchDepartmentDetails(departmentId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: departmentId !== undefined,
    ...config,
  });

export const getDepartmentDetails = async (departmentId: string) =>
  queryClient.fetchQuery({
    queryKey: [QUERY_KEY.DEPARTMENT_DETAILS, departmentId],
    queryFn: () => fetchDepartmentDetails(departmentId),
  });
